import { getAccessToken } from "./supabase";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8080";

export interface HermesGrokStatus {
  ok: boolean;
  provider: "xai-oauth";
  appProvider: "hermes-grok";
  available: boolean;
  status: "connected" | "missing" | "unavailable" | "error";
  model: string;
  checkedAt: string;
  detail: string;
  connectCommand: "hermes model";
  directAuthCommand: "hermes auth add xai-oauth";
  authStoreExposed: false;
}

export interface ManagedHermesRuntime {
  userId: string;
  status: "stopped" | "starting" | "running" | "stopping" | "error";
  oauthStatus: "missing" | "pending" | "connected" | "revoked";
  provider: "xai-oauth";
  model: string;
  idleTimeoutSeconds: number;
  dailyCapUsd: number;
  budgetStatus: "ok" | "capped";
  updatedAt: string;
  authStoreExposed: false;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function fetchHermesGrokStatus(): Promise<HermesGrokStatus> {
  const res = await fetch(`${API_BASE}/api/hermes/grok/status`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Grok status unavailable");
  return (await res.json()) as HermesGrokStatus;
}

export async function rescanHermesGrokStatus(): Promise<HermesGrokStatus> {
  const res = await fetch(`${API_BASE}/api/hermes/grok/rescan`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("Grok rescan unavailable");
  return (await res.json()) as HermesGrokStatus;
}

export async function fetchManagedHermesRuntime(): Promise<ManagedHermesRuntime | null> {
  const res = await fetch(`${API_BASE}/api/hermes/cloud-runtime/me`, {
    credentials: "include",
    headers: await authHeaders(),
  });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error("Cloud runtime unavailable");
  const data = await res.json();
  return data.runtime as ManagedHermesRuntime;
}

export async function startManagedHermesRuntime(): Promise<ManagedHermesRuntime> {
  const res = await fetch(`${API_BASE}/api/hermes/cloud-runtime/start`, {
    method: "POST",
    credentials: "include",
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error("Cloud runtime start failed");
  const data = await res.json();
  return data.runtime as ManagedHermesRuntime;
}

export async function startManagedHermesOAuth(): Promise<{
  runtime: ManagedHermesRuntime;
  command: string;
}> {
  const res = await fetch(`${API_BASE}/api/hermes/cloud-runtime/oauth/start`, {
    method: "POST",
    credentials: "include",
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error("Cloud OAuth start failed");
  return (await res.json()) as {
    runtime: ManagedHermesRuntime;
    command: string;
  };
}

export async function completeManagedHermesOAuth(): Promise<ManagedHermesRuntime> {
  const res = await fetch(
    `${API_BASE}/api/hermes/cloud-runtime/oauth/complete`,
    {
      method: "POST",
      credentials: "include",
      headers: await authHeaders(),
    },
  );
  if (!res.ok) throw new Error("Cloud OAuth completion failed");
  const data = await res.json();
  return data.runtime as ManagedHermesRuntime;
}
